import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const StudentForm = ({ open, onClose, onSubmit, student, uploading }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    picture_url: '',
    achievements: '',
    achievement_type: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'

  useEffect(() => {
    if (student) {
      setFormData({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        picture_url: student.picture_url || '',
        achievements: student.achievements || '',
        achievement_type: student.achievement_type || '',
      });
      setPreviewUrl(student.picture_url || '');
      setUploadMode('url');
      setSelectedFile(null);
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        picture_url: '',
        achievements: '',
        achievement_type: '',
      });
      setPreviewUrl('');
      setUploadMode('url');
      setSelectedFile(null);
    }
  }, [student, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update preview if URL changes
    if (name === 'picture_url') {
      setPreviewUrl(value);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadMode('file');

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);

      // Clear URL input when file is selected
      setFormData((prev) => ({
        ...prev,
        picture_url: '',
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate that picture is provided (either file or URL)
    if (uploadMode === 'file' && !selectedFile) {
      return;
    }
    if (uploadMode === 'url' && !formData.picture_url) {
      return;
    }

    onSubmit(formData, selectedFile);
  };

  const switchToUrl = () => {
    setUploadMode('url');
    setSelectedFile(null);
    setPreviewUrl(formData.picture_url);
  };

  const switchToFile = () => {
    setUploadMode('file');
    setFormData((prev) => ({
      ...prev,
      picture_url: '',
    }));
    setPreviewUrl('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{student ? 'Сурагч засах' : 'Шинэ Сурагч нэмэх'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <TextField
              label='Овог'
              name='first_name'
              value={formData.first_name}
              onChange={handleChange}
              required
              fullWidth
              disabled={uploading}
            />
            <TextField
              label='Нэр'
              name='last_name'
              value={formData.last_name}
              onChange={handleChange}
              required
              fullWidth
              disabled={uploading}
            />
            <TextField
              label='Амжилт'
              name='achievements'
              value={formData.achievements}
              onChange={handleChange}
              required
              fullWidth
              disabled={uploading}
              multiline
              rows={3}
              placeholder='Оюутны амжилт, шагнал урамшуулал...'
            />

            <FormControl fullWidth disabled={uploading} required>
              <InputLabel>Амжилтын төрөл</InputLabel>
              <Select
                name='achievement_type'
                value={formData.achievement_type}
                onChange={handleChange}
                label='Амжилтын төрөл'
                required
              >
                <MenuItem value=''>
                  <em>Сонгоогүй</em>
                </MenuItem>
                <MenuItem value='International'>Олон улсын</MenuItem>
                <MenuItem value='National'>Үндэсний</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Зураг
              </Typography>

              <Box display='flex' gap={1} mb={2}>
                <Button
                  size='small'
                  variant={uploadMode === 'file' ? 'contained' : 'outlined'}
                  onClick={switchToFile}
                  disabled={uploading}
                >
                  Файл оруулах
                </Button>
                <Button
                  size='small'
                  variant={uploadMode === 'url' ? 'contained' : 'outlined'}
                  onClick={switchToUrl}
                  disabled={uploading}
                >
                  URL оруулах
                </Button>
              </Box>

              {uploadMode === 'file' ? (
                <Box>
                  <Button
                    variant='outlined'
                    component='label'
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    disabled={uploading}
                  >
                    {selectedFile ? selectedFile.name : 'Зураг сонгох'}
                    <input type='file' hidden accept='image/*' onChange={handleFileSelect} />
                  </Button>
                  {selectedFile && (
                    <Alert severity='info' sx={{ mt: 1 }}>
                      Сонгосон файл: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </Alert>
                  )}
                </Box>
              ) : (
                <TextField
                  label='Зургийн URL'
                  name='picture_url'
                  value={formData.picture_url}
                  onChange={handleChange}
                  required
                  fullWidth
                  disabled={uploading}
                  placeholder='https://example.com/image.jpg'
                />
              )}

              {previewUrl && (
                <Box mt={2} display='flex' justifyContent='center'>
                  <Avatar src={previewUrl} alt='Урьдчилсан үзэмж' sx={{ width: 120, height: 120 }} />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={uploading}>
            Цуцлах
          </Button>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={uploading}
            startIcon={uploading && <CircularProgress size={20} />}
          >
            {uploading ? 'Илгээж байна...' : student ? 'Засах' : 'Нэмэх'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default StudentForm;
